import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
    Target, Check, X, ArrowLeft, Crown, Zap, Sparkles,
    Lock, Star, Mail, Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = {
    free: {
        name: "Free",
        price: 0,
        description: "Get started with the basics",
        features: [
            { text: "Unlimited games", included: true },
            { text: "4 resume tones", included: true },
            { text: "PDF export with watermark", included: true },
            { text: "Share resume link", included: true },
            { text: "Skill Gap Detector", included: true },
            { text: "Job Recommendations", included: true },
            { text: "Pro resume tones", included: false },
            { text: "Remove watermark", included: false }
        ],
        tones: ["Professional / Corporate", "Student / Entry-Level", "Technical / STEM", "Confident (Not Arrogant)"]
    },
    pro: {
        name: "Pro",
        monthly: 7.99,
        yearly: 59.99,
        description: "For serious job seekers",
        popular: true,
        features: [
            { text: "Unlimited games", included: true },
            { text: "11 resume tones", included: true },
            { text: "PDF export (no watermark)", included: true },
            { text: "Share resume link", included: true },
            { text: "ATS-optimized wording", included: true },
            { text: "Multiple resume styles", included: true },
            { text: "Premium ATS tones", included: false },
            { text: "Job-specific tailoring", included: false }
        ],
        tones: ["+ Leadership", "+ Analytical", "+ Problem-Solver", "+ Creative / Media", "+ Startup / Fast-Paced", "+ Customer-Facing", "+ Team-Focused"]
    },
    premium: {
        name: "Premium",
        monthly: 19.99,
        yearly: 149.99,
        description: "Maximum career impact",
        features: [
            { text: "Everything in Pro", included: true },
            { text: "15 resume tones total", included: true },
            { text: "ATS-Max optimization", included: true },
            { text: "Quantified Impact tone", included: true },
            { text: "Executive Polish tone", included: true },
            { text: "Military-Style Clarity", included: true },
            { text: "Priority support", included: true },
            { text: "Job-specific tailoring", included: true }
        ],
        tones: ["+ ATS-Max", "+ Quantified Impact", "+ Executive Polish", "+ Military-Style Clarity"]
    }
};

export default function Pricing() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isAuthenticated, getAuthHeaders, refreshUser } = useAuth();
    const [billingPeriod, setBillingPeriod] = useState('yearly');
    const [loading, setLoading] = useState(null);
    const [switchingTier, setSwitchingTier] = useState(null);

    useEffect(() => {
        if (searchParams.get('cancelled') === 'true') {
            toast.error('Payment was cancelled');
        }
    }, [searchParams]);

    // DEV MODE: Switch tier directly
    const handleDevSwitchTier = async (tier) => {
        setSwitchingTier(tier);
        try {
            await axios.post(`${API}/dev/switch-tier`, { tier }, getAuthHeaders());
            toast.success(`Switched to ${tier.toUpperCase()} tier!`);
            await refreshUser();
            navigate('/dashboard');
        } catch (error) {
            toast.error('Failed to switch tier');
        } finally {
            setSwitchingTier(null);
        }
    };

    const handleSubscribe = async (tier) => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/pricing');
            return;
        }

        if (tier === 'free') {
            navigate('/dashboard');
            return;
        }

        setLoading(tier);
        try {
            const planId = `${tier}_${billingPeriod === 'yearly' ? 'yearly' : 'monthly'}`;
            const response = await axios.post(`${API}/subscribe`, {
                plan_id: planId,
                origin_url: window.location.origin
            }, getAuthHeaders());

            // Redirect to Stripe checkout
            window.location.href = response.data.checkout_url;
        } catch (error) {
            toast.error('Failed to start checkout');
            setLoading(null);
        }
    };

    const userTier = user?.tier || 'free';
    const yearlySavings = {
        pro: Math.round((PLANS.pro.monthly * 12 - PLANS.pro.yearly) / (PLANS.pro.monthly * 12) * 100),
        premium: Math.round((PLANS.premium.monthly * 12 - PLANS.premium.yearly) / (PLANS.premium.monthly * 12) * 100)
    };

    return (
        <div className="min-h-screen bg-[#09090b]">
            {/* Header */}
            <header className="border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Target className="w-6 h-6 text-primary" />
                        <span className="font-bold text-lg tracking-tight">LevelUp Resume</span>
                    </Link>
                    {isAuthenticated ? (
                        <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground">
                            <ArrowLeft className="w-4 h-4 inline mr-2" />
                            Back to Dashboard
                        </Link>
                    ) : (
                        <Link to="/login" className="text-sm text-primary hover:underline">
                            Login
                        </Link>
                    )}
                </div>
            </header>

            {/* Content */}
            <main className="max-w-6xl mx-auto px-6 py-16">
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                        Choose Your <span className="text-primary">Level</span>
                    </h1>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Unlock more resume tones, remove watermarks, and get ATS-optimized content
                    </p>
                </div>

                {/* Billing Toggle */}
                <div className="flex items-center justify-center gap-4 mb-12">
                    <button
                        onClick={() => setBillingPeriod('monthly')}
                        className={`px-4 py-2 rounded-lg transition-all ${
                            billingPeriod === 'monthly' 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-secondary text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingPeriod('yearly')}
                        className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
                            billingPeriod === 'yearly' 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-secondary text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Yearly
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                            Save up to {yearlySavings.premium}%
                        </span>
                    </button>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Free Plan */}
                    <div className="glass rounded-xl p-8 flex flex-col">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold mb-1">{PLANS.free.name}</h3>
                            <p className="text-sm text-muted-foreground">{PLANS.free.description}</p>
                        </div>
                        <div className="mb-6">
                            <span className="text-4xl font-bold">$0</span>
                            <span className="text-muted-foreground">/forever</span>
                        </div>
                        <ul className="space-y-3 mb-8 flex-grow">
                            {PLANS.free.features.map((feature, i) => (
                                <li key={`free-feature-${feature.text}`} className="flex items-center gap-2 text-sm">
                                    {feature.included ? (
                                        <Check className="w-4 h-4 text-green-500" />
                                    ) : (
                                        <X className="w-4 h-4 text-muted-foreground" />
                                    )}
                                    <span className={feature.included ? '' : 'text-muted-foreground'}>
                                        {feature.text}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mb-6">
                            <p className="text-xs text-muted-foreground mb-2">Available tones:</p>
                            <div className="flex flex-wrap gap-1">
                                {PLANS.free.tones.map((tone, i) => (
                                    <span key={`free-tone-${tone}`} className="text-xs px-2 py-1 bg-secondary rounded">
                                        {tone}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <Button 
                            data-testid="select-free-btn"
                            variant={userTier === 'free' ? 'secondary' : 'outline'}
                            onClick={() => handleSubscribe('free')}
                            disabled={userTier === 'free'}
                            className="w-full"
                        >
                            {userTier === 'free' ? 'Current Plan' : 'Get Started'}
                        </Button>
                    </div>

                    {/* Pro Plan */}
                    <div className="glass rounded-xl p-8 flex flex-col border-2 border-primary relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            MOST POPULAR
                        </div>
                        <div className="mb-6">
                            <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                                <Zap className="w-5 h-5 text-primary" />
                                {PLANS.pro.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">{PLANS.pro.description}</p>
                        </div>
                        <div className="mb-6">
                            <span className="text-4xl font-bold">
                                ${billingPeriod === 'yearly' ? PLANS.pro.yearly : PLANS.pro.monthly}
                            </span>
                            <span className="text-muted-foreground">
                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                            </span>
                            {billingPeriod === 'yearly' && (
                                <p className="text-xs text-green-400 mt-1">
                                    Save {yearlySavings.pro}% vs monthly
                                </p>
                            )}
                        </div>
                        <ul className="space-y-3 mb-8 flex-grow">
                            {PLANS.pro.features.map((feature, i) => (
                                <li key={`pro-feature-${feature.text}`} className="flex items-center gap-2 text-sm">
                                    {feature.included ? (
                                        <Check className="w-4 h-4 text-green-500" />
                                    ) : (
                                        <Lock className="w-4 h-4 text-muted-foreground" />
                                    )}
                                    <span className={feature.included ? '' : 'text-muted-foreground'}>
                                        {feature.text}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <div className="mb-6">
                            <p className="text-xs text-muted-foreground mb-2">Unlocked tones:</p>
                            <div className="flex flex-wrap gap-1">
                                {PLANS.pro.tones.map((tone, i) => (
                                    <span key={`pro-tone-${tone}`} className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">
                                        {tone}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <Button 
                            data-testid="select-pro-btn"
                            onClick={() => handleSubscribe('pro')}
                            disabled={loading === 'pro' || userTier === 'pro'}
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                            {loading === 'pro' ? 'Processing...' : userTier === 'pro' ? 'Current Plan' : 'Upgrade to Pro'}
                        </Button>
                    </div>

                    {/* Premium Plan */}
                    <div className="glass rounded-xl p-8 flex flex-col">
                        <div className="mb-6">
                            <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
                                <Crown className="w-5 h-5 text-yellow-500" />
                                {PLANS.premium.name}
                            </h3>
                            <p className="text-sm text-muted-foreground">{PLANS.premium.description}</p>
                        </div>
                        <div className="mb-6">
                            <span className="text-4xl font-bold">
                                ${billingPeriod === 'yearly' ? PLANS.premium.yearly : PLANS.premium.monthly}
                            </span>
                            <span className="text-muted-foreground">
                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                            </span>
                            {billingPeriod === 'yearly' && (
                                <p className="text-xs text-green-400 mt-1">
                                    Save {yearlySavings.premium}% vs monthly
                                </p>
                            )}
                        </div>
                        <ul className="space-y-3 mb-8 flex-grow">
                            {PLANS.premium.features.map((feature, i) => (
                                <li key={`premium-feature-${feature.text}`} className="flex items-center gap-2 text-sm">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>{feature.text}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mb-6">
                            <p className="text-xs text-muted-foreground mb-2">Exclusive tones:</p>
                            <div className="flex flex-wrap gap-1">
                                {PLANS.premium.tones.map((tone, i) => (
                                    <span key={`premium-tone-${tone}`} className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                                        {tone}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <Button 
                            data-testid="select-premium-btn"
                            onClick={() => handleSubscribe('premium')}
                            disabled={loading === 'premium' || userTier === 'premium'}
                            className="w-full border-yellow-500/50 hover:bg-yellow-500/10"
                            variant="outline"
                        >
                            {loading === 'premium' ? 'Processing...' : userTier === 'premium' ? 'Current Plan' : 'Go Premium'}
                        </Button>
                    </div>
                </div>

                {/* DEV MODE: Tier Switcher - Admin Only */}
                {isAuthenticated && user?.email === 'alexhansen2701@gmail.com' && (
                    <div className="mt-12 p-6 border border-dashed border-yellow-500/50 rounded-xl bg-yellow-500/5">
                        <div className="flex items-center gap-2 mb-4">
                            <Zap className="w-5 h-5 text-yellow-500" />
                            <h3 className="font-bold text-yellow-400">Dev Mode: Instant Tier Switch</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                            Test different subscription tiers without payment. Your current tier: <span className="text-primary font-bold uppercase">{userTier}</span>
                        </p>
                        <div className="flex gap-3">
                            <Button
                                data-testid="dev-switch-free"
                                onClick={() => handleDevSwitchTier('free')}
                                disabled={switchingTier === 'free' || userTier === 'free'}
                                variant="outline"
                                className="flex-1 border-white/20"
                            >
                                {switchingTier === 'free' ? 'Switching...' : 'Switch to Free'}
                            </Button>
                            <Button
                                data-testid="dev-switch-pro"
                                onClick={() => handleDevSwitchTier('pro')}
                                disabled={switchingTier === 'pro' || userTier === 'pro'}
                                variant="outline"
                                className="flex-1 border-primary/50 text-primary"
                            >
                                {switchingTier === 'pro' ? 'Switching...' : 'Switch to Pro'}
                            </Button>
                            <Button
                                data-testid="dev-switch-premium"
                                onClick={() => handleDevSwitchTier('premium')}
                                disabled={switchingTier === 'premium' || userTier === 'premium'}
                                variant="outline"
                                className="flex-1 border-yellow-500/50 text-yellow-400"
                            >
                                {switchingTier === 'premium' ? 'Switching...' : 'Switch to Premium'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* FAQ or Trust Badges */}
                <div className="mt-16 text-center space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Secure payments powered by Stripe. Cancel anytime.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
                        <span>Need help?</span>
                        <a 
                            href="mailto:alexhansen2701@gmail.com" 
                            className="hover:text-foreground transition-colors flex items-center gap-2"
                        >
                            <Mail className="w-4 h-4" />
                            alexhansen2701@gmail.com
                        </a>
                        <a 
                            href="tel:+61469305935" 
                            className="hover:text-foreground transition-colors flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" />
                            +61 469 305 935
                        </a>
                    </div>
                </div>
            </main>
        </div>
    );
}
